import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';

export const HERO_IMAGE = 'https://images.unsplash.com/photo-1693031630189-a39e6d70bf22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwcHJpbnRpbmclMjBwcmVzcyUyMG1hY2hpbmUlMjBmYWN0b3J5fGVufDB8fHx8MTc3NjY3NDQwMnww&ixlib=rb-4.1.0&q=85';
export const IMG_MATERIALS = 'https://images.unsplash.com/photo-1681726267024-04427f16ad45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxjYXJkYm9hcmQlMjBwYXBlciUyMHJvbGxzJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc3NjY3NDQwMnww&ixlib=rb-4.1.0&q=85';
export const IMG_SPARE = 'https://images.unsplash.com/photo-1742729096780-600245031d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwbWFjaGluZSUyMHNwYXJlJTIwcGFydHMlMjBnZWFyfGVufDB8fHx8MTc3NjY3NDQwMnww&ixlib=rb-4.1.0&q=85';
export const IMG_MACHINES = 'https://images.unsplash.com/photo-1707263899445-601d5c695598?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwcHJpbnRpbmclMjBwcmVzcyUyMG1hY2hpbmUlMjBmYWN0b3J5fGVufDB8fHx8MTc3NjY3NDQwMnww&ixlib=rb-4.1.0&q=85';
export const IMG_PAINTS = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85';

export const WIDTH_MIN = 0;
export const WIDTH_MAX = 2000;
export const WIDTH_STEP = 10;

export const WidthSlicerPopover = ({ minWidth, maxWidth, onApply, onClear, t }) => {
  const [range, setRange] = useState([
    minWidth ? Number(minWidth) : WIDTH_MIN,
    maxWidth ? Number(maxWidth) : WIDTH_MAX,
  ]);

  useEffect(() => {
    setRange([
      minWidth ? Number(minWidth) : WIDTH_MIN,
      maxWidth ? Number(maxWidth) : WIDTH_MAX,
    ]);
  }, [minWidth, maxWidth]);

  const handleSliderCommit = (value) => {
    const min = value[0] === WIDTH_MIN ? '' : String(value[0]);
    const max = value[1] === WIDTH_MAX ? '' : String(value[1]);
    onApply(min, max);
  };

  const handleInputChange = (index, raw) => {
    const val =
      raw === ''
        ? index === 0
          ? WIDTH_MIN
          : WIDTH_MAX
        : Math.max(WIDTH_MIN, Math.min(WIDTH_MAX, Number(raw)));
    const next = [...range];
    next[index] = val;
    if (next[0] > next[1]) next[index === 0 ? 1 : 0] = val;
    setRange(next);
    const min = next[0] === WIDTH_MIN ? '' : String(next[0]);
    const max = next[1] === WIDTH_MAX ? '' : String(next[1]);
    onApply(min, max);
  };

  const isActive = range[0] !== WIDTH_MIN || range[1] !== WIDTH_MAX;

  return (
    <div className="space-y-4" data-testid="home-width-slicer-content">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t('filter.width', 'Breite (mm)')}</span>
        {isActive && (
          <button
            onClick={() => {
              setRange([WIDTH_MIN, WIDTH_MAX]);
              onClear();
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="home-width-slicer-clear"
          >
            {t('common.reset', 'Zurücksetzen')}
          </button>
        )}
      </div>
      <Slider
        value={range}
        min={WIDTH_MIN}
        max={WIDTH_MAX}
        step={WIDTH_STEP}
        onValueChange={setRange}
        onValueCommit={handleSliderCommit}
        className="my-4"
        data-testid="home-width-range-slider"
      />
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={WIDTH_MIN}
          max={WIDTH_MAX}
          value={range[0]}
          onChange={(e) => handleInputChange(0, e.target.value)}
          className="h-8 text-sm text-center"
          data-testid="home-width-slicer-min"
        />
        <span className="text-muted-foreground text-xs">–</span>
        <Input
          type="number"
          min={WIDTH_MIN}
          max={WIDTH_MAX}
          value={range[1]}
          onChange={(e) => handleInputChange(1, e.target.value)}
          className="h-8 text-sm text-center"
          data-testid="home-width-slicer-max"
        />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
        <span>0 mm</span>
        <span>500</span>
        <span>1000</span>
        <span>1500</span>
        <span>2000 mm</span>
      </div>
    </div>
  );
};

export const SectionHeading = ({ label, title, subtitle, accentColor, cta, testId }) => (
  <div className="flex items-end justify-between gap-4 mb-6 flex-wrap" data-testid={testId}>
    <div className="max-w-2xl">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-[2px] w-8" style={{ backgroundColor: accentColor }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
          {label}
        </span>
      </div>
      <h2 className="font-manrope text-2xl md:text-3xl font-black tracking-tight text-slate-900 dark:text-slate-50 leading-tight">
        {title}
      </h2>
      {subtitle && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{subtitle}</p>}
    </div>
    {cta}
  </div>
);
